// AI: This new component encapsulates the client editing form and logic in a dialog.
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { Client, LeadSource, Partner } from 'src/app/api/models';
import { ClientsService } from 'src/app/api/services';
import { AAARatings } from 'src/app/enum/aaa-ratings';
import { CountriesMap, CountriesMapCache } from 'src/app/model/model-map-cache';
import { LeadSource as LegacyLeadSource } from 'src/app/model/lead-source';
import { Money } from 'src/app/model/money';
import { Partner as LegacyPartner } from 'src/app/model/partner';
import { canonicalizeBusinessIdControl, clientBusinessIdValidator } from 'src/app/validators/business-id/angular-adapter';

@Component({
  selector: 'app-client-edit-dialog',
  templateUrl: './client-edit-dialog.component.html',
  styleUrls: ['./client-edit-dialog.component.scss']
})
export class ClientEditDialogComponent implements OnInit {

  clientForm: UntypedFormGroup;
  client: Client;
  countries$: Observable<CountriesMap>;
  aaaRatings = AAARatings;

  selectingLeadSource = false;
  selectingResponsibleSeller = false;
  selectedLeadSource: LegacyLeadSource;
  selectedResponsibleSeller: LegacyPartner;

  constructor(
    public dialogRef: MatDialogRef<ClientEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { client: Client },
    private builder: UntypedFormBuilder,
    private clientsService: ClientsService,
    private countriesMapCache: CountriesMapCache,
  ) {
    this.client = { ...data.client }; // Create a clone to avoid modifying the original object
  }

  ngOnInit(): void {
    this.countries$ = this.countriesMapCache.map$;
    this.initializeForm();
    this.patchForm();
  }

  initializeForm(): void {
    this.clientForm = this.builder.group({
      business_id: [{ value: null, disabled: true }, [Validators.required, clientBusinessIdValidator()]],
      country: ['', Validators.required],
      name: ['', Validators.required],
      company_form_code: [null],
      company_form_other: [null],
      is_in_prepayment_register: [{ value: null, disabled: true }],
      mark_prepayment_register_checked: [false],
      lead_source_id: [null],
      responsible_seller_id: [null],
      has_credit_limit: [false],
      credit_limit: this.builder.group({
        limit: this.builder.group({
          amount: [0, [Validators.min(0)]],
          currency: ['EUR'],
        }),
      }),
      has_aaa_rating: [false],
      aaa_rating: this.builder.group({
        category: [null],
      }),
    });

    this.clientForm.get('country').valueChanges
      .subscribe((country) => {
        const businessId = this.clientForm.get('business_id');
        if (businessId) {
          businessId.markAsTouched();
          businessId.updateValueAndValidity();
        }

        const companyFormCode = this.clientForm.get('company_form_code');
        const companyFormOther = this.clientForm.get('company_form_other');

        if (country === 'FI') {
          companyFormCode.setValidators(Validators.required);
          companyFormOther.clearValidators();
          companyFormOther.setValue(null);
        } else {
          companyFormCode.clearValidators();
          companyFormCode.setValue(null);
          companyFormOther.setValidators(Validators.required);
        }
        companyFormCode.updateValueAndValidity();
        companyFormOther.updateValueAndValidity();
      });

    this.clientForm.get('mark_prepayment_register_checked').valueChanges.subscribe(checked => {
      const control = this.clientForm.get('is_in_prepayment_register');
      if (checked) {
        control.enable();
      } else {
        control.disable();
      }
    });
  }

  patchForm(): void {
    this.clientForm.patchValue({
      ...this.client,
      is_in_prepayment_register: this.client.prepayment_register?.is_in_register,
      has_credit_limit: !!this.client.credit_limit,
      has_aaa_rating: !!this.client.aaa_rating,
    });
    this.clientForm.get('mark_prepayment_register_checked').setValue(false);
    this.selectedLeadSource = this.convertToLegacyLeadSource(this.client.lead_source);
    this.selectedResponsibleSeller = this.convertToLegacyPartner(this.client.responsible_seller);
  }

  save(): void {
    if (this.clientForm.invalid) {
      return;
    }

    // business_id is disabled here (a stored value), so the helper skips it and
    // getRawValue() submits it byte-identical — no manufactured change for the
    // client validate-on-change/unique-index guards to reject (design D10).
    canonicalizeBusinessIdControl(this.clientForm.get('business_id'));

    const formValue = this.clientForm.getRawValue();
    const { is_in_prepayment_register, mark_prepayment_register_checked, ...formModel } = formValue;
    Object.assign(this.client, formModel);

    if (mark_prepayment_register_checked) {
      this.client.prepayment_register = {
        is_in_register: is_in_prepayment_register,
        mark_checked: true,
      };
    }

    if (this.client.credit_limit) {
        this.client.credit_limit.limit = new Money(this.client.credit_limit.limit);
    }

    if (!this.clientForm.value.has_credit_limit) {
      this.client.credit_limit = null;
    }

    if (!this.clientForm.value.has_aaa_rating) {
      this.client.aaa_rating = null;
    }

    this.clientsService.updateClient({ id: this.client.id, body: this.client })
      .pipe(take(1))
      .subscribe(() => {
        this.dialogRef.close(true);
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  // AI: Lead source and responsible seller selection logic, moved from client-page
  selectLeadSource() { this.selectingLeadSource = true; }
  leadSourceSelected(leadSource?: LegacyLeadSource) {
    this.selectingLeadSource = false;
    this.selectedLeadSource = leadSource;
    this.clientForm.patchValue({ "lead_source_id": leadSource?.leadSourceId });
  }
  leadSourceCleared() {
    this.selectingLeadSource = false;
    this.selectedLeadSource = null;
    this.clientForm.patchValue({ "lead_source_id": null });
  }
  selectResponsibleSeller() { this.selectingResponsibleSeller = true; }
  responsibleSellerSelected(responsibleSeller?: LegacyPartner) {
    this.selectingResponsibleSeller = false;
    this.selectedResponsibleSeller = responsibleSeller;
    this.clientForm.patchValue({ "responsible_seller_id": responsibleSeller?.partnerId });
  }
  responsibleSellerCleared() {
    this.selectingResponsibleSeller = false;
    this.selectedResponsibleSeller = null;
    this.clientForm.patchValue({ "responsible_seller_id": null });
  }

  // AI: Conversion helpers, moved from client-page
  convertToLegacyPartner(from: Partner): LegacyPartner {
    if (!from) return null;
    const legacy = new LegacyPartner();
    legacy.partnerId = from.id;
    legacy.name = from.name;
    return legacy;
  }
  convertToLegacyLeadSource(from: LeadSource): LegacyLeadSource {
    if (!from) return null;
    const legacy = new LegacyLeadSource();
    legacy.leadSourceId = from.id;
    legacy.name = from.name;
    return legacy;
  }
}
